import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { PlusIcon, MinusIcon } from 'lucide-react';
const faqs = [{
  question: 'How does Emigo work?',
  answer: "When you tap a feature like Reframe, Interpret, or Create, Emigo analyzes your message differently depending on what you need.\n\n• Reframe helps you rewrite something you've already drafted.\n\n• Interpret helps you understand or respond to someone else's message by analyzing tone, intent, and meaning.\n\n• Create helps you craft something new from scratch, like a post, text, or heartfelt message.\n\nEmigo only processes text when you use a feature, and otherwise stays completely offline."
}, {
  question: 'Does Emigo read my messages?',
  answer: 'Emigo has no access to your chats. It only analyzes the text or screenshots you choose when using a feature like Reframe or Interpret. Everything is processed securely and never tied to your identity.'
}, {
  question: 'How does Emigo handle my personal information?',
  answer: 'Emigo does not require you to create an account or provide any personal information. It has no access to your private messages or conversations. When you use a feature, only the text or screenshots you choose to analyze are securely processed to generate a response. This data is handled confidentially, used only for that request, and not stored by Emigo, sold, or linked to your identity.'
}, {
  question: 'Is my data private and secure?',
  answer: "Yes. Your data is private and secure. Emigo doesn't track, sell, or link anything to you. When you use a feature, your text is processed securely in a controlled cloud environment. Emigo does not store or save any local data on your device."
}, {
  question: 'Can Emigo further personalize my responses?',
  answer: "For now, you can personalize Emigo by using the edit feature: just tell it how you'd like a response changed, and it learns your preferences for that session. In the future, we'll introduce optional personalization that can adapt to your tone and emotional style over time, but only if you choose to turn it on."
}];
const FAQItem = ({
  question,
  answer,
  isLast
}: {
  question: string;
  answer: string;
  isLast: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div style={{
    borderBottom: !isLast ? '1px solid var(--border-faint)' : 'none'
  }} data-id="element-43">
      <button className="flex justify-between items-start w-full text-left py-6 md:py-7 group transition-colors duration-200" onClick={() => setIsOpen(!isOpen)} data-id="element-44">
        <span className="text-lg md:text-xl font-normal pr-8 transition-colors duration-200" style={{
        color: 'var(--navy)',
        fontFamily: "'DM Serif Display', serif"
      }} data-id="element-45">
          {question}
        </span>
        <div className="flex-shrink-0 mt-1.5" data-id="element-46">
          {isOpen ? <MinusIcon className="h-5 w-5" style={{
          color: 'var(--coral)'
        }} data-id="element-47" /> : <PlusIcon className="h-5 w-5 transition-transform duration-200 group-hover:scale-110" style={{
          color: 'var(--coral)'
        }} data-id="element-48" />}
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-400 ease-in-out ${isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-49">
        <div className="pb-6" data-id="element-50">
          <p className="leading-relaxed whitespace-pre-line text-base" style={{
          color: 'var(--muted)'
        }} data-id="element-51">
            {answer}
          </p>
        </div>
      </div>
    </div>;
};
const FAQSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(entries => entries[0].isIntersecting && setVisible(true), {
      threshold: 0.1
    });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return <section id="faq" ref={ref} className="relative py-24 md:py-32" style={{
    backgroundColor: 'var(--ivory)'
  }} data-id="element-52">
      <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-10" data-id="element-53">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={visible ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }} className="mb-12 md:mb-16" data-id="element-54">
          <p className="text-xs tracking-widest uppercase mb-4" style={{
          color: 'var(--coral)'
        }} data-id="element-55">
            Answers
          </p>
          <h2 className="text-4xl md:text-5xl font-normal leading-[1.1] tracking-tight" style={{
          color: 'var(--navy)',
          fontFamily: "'DM Serif Display', serif"
        }} data-id="element-56">
            Frequently asked.
          </h2>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={visible ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8,
        delay: 0.15,
        ease: [0.16, 1, 0.3, 1]
      }} data-id="element-57">
          {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} isLast={index === faqs.length - 1} data-id="element-58" />)}
        </motion.div>
      </div>
    </section>;
};
export default FAQSection;